// PersonalDocuments.js
import React, { useEffect, useState } from 'react';
import { useTranslation } from '../i18n/i18n';
import API_ENDPOINTS from '../config/apiConfig';
import {
  TextField, MenuItem, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Box, Chip, useTheme, useMediaQuery, Autocomplete, Paper,
  IconButton, LinearProgress, Fade, Avatar, Alert
} from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PersonIcon from '@mui/icons-material/Person';
import HomeIcon from '@mui/icons-material/Home';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import FixedSnackbar from './FixedSnackbar';

const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];

// Fallback personal info field definitions (used if backend is unavailable)
const defaultPersonalInfoFields = [
  {
    key: 'curp',
    label: 'CURP',
    required: true,
    validation: { type: 'text', pattern: '^[A-Za-z0-9]{18}$', maxLength: 18, uppercase: true }
  },
  {
    key: 'rfc',
    label: 'RFC',
    required: true,
    validation: { type: 'text', pattern: '^[A-Za-z0-9]{13}$', maxLength: 13, uppercase: true }
  },
  {
    key: 'degree',
    label: 'Nivel de estudios',
    required: true,
    validation: {
      type: 'select',
      options: [
        'degree_jrhigh', 'degree_highschool', 'degree_techdegree',
        'degree_collegeinprogress', 'degree_college', 'degree_masters', 'degree_doctors'
      ]
    }
  },
  {
    key: 'imss',
    label: 'Número de Seguro Social IMSS',
    required: true,
    validation: { type: 'text', pattern: '^[A-Za-z0-9]{11}$', maxLength: 11 }
  },
  {
    key: 'clabe',
    label: 'CLABE Interbancaria',
    required: true,
    validation: { type: 'text', pattern: '^[0-9]{18}$', maxLength: 18, digitsOnly: true }
  }
];

const initialPersonalInfo = {};

const initialAddress = {
  street: '',
  numExt: '',
  numInt: '',
  neigborhood: '',
  cp: '',
  city: '',
  state: '',
  country: ''
};

// Section Card Component
function SectionCard({ title, icon, children, sx = {} }) {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2.5, sm: 3 },
        mb: 3,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        transition: 'box-shadow 0.2s ease-in-out',
        '&:hover': {
          boxShadow: `0 1px 3px ${theme.palette.action.hover}`
        },
        ...sx
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Avatar
          sx={{
            width: 36,
            height: 36,
            backgroundColor: theme.palette.primary.main + '14',
            color: theme.palette.primary.main
          }}
        >
          {icon}
        </Avatar>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>
      {children}
    </Paper>
  );
}

// Document Row Component
function DocumentRow({ doc, docItem, uploading, onUpload, onPreview }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isUploading = uploading[doc.key];

  const getDocLabel = () => {
    const transKey = `personaldocs_doc_${doc.key}`;
    const translated = t(transKey);
    return (translated && translated !== transKey) ? translated : (doc.label || doc.key);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5 },
        mb: 1.5,
        borderRadius: 1.5,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        transition: 'all 0.15s ease-in-out',
        opacity: isUploading ? 0.7 : 1,
        '&:hover': {
          borderColor: theme.palette.primary.light,
          backgroundColor: theme.palette.action.hover
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 0 } }}>
        {/* Left: Icon + Info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              backgroundColor: docItem ? theme.palette.success.main + '14' : theme.palette.grey[100],
              color: docItem ? theme.palette.success.main : theme.palette.grey[500]
            }}
          >
            <InsertDriveFileIcon />
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: 500, lineHeight: 1.4 }}>
              {getDocLabel()}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              {docItem ? (
                <Chip
                  icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                  label={t('personaldocs_status_uploaded')}
                  size="small"
                  sx={{
                    height: 24,
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    backgroundColor: theme.palette.success.main + '14',
                    color: theme.palette.success.dark,
                    border: `1px solid ${theme.palette.success.main}30`,
                    '& .MuiChip-icon': { color: 'inherit' }
                  }}
                />
              ) : (
                <Chip
                  icon={<ErrorOutlineIcon sx={{ fontSize: 16 }} />}
                  label={t('personaldocs_status_missing')}
                  size="small"
                  sx={{
                    height: 24,
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    backgroundColor: theme.palette.warning.main + '14',
                    color: theme.palette.warning.dark,
                    border: `1px solid ${theme.palette.warning.main}30`,
                    '& .MuiChip-icon': { color: 'inherit' }
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>

        {/* Right: Actions */}
        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}>
          {docItem ? (
            <>
              <Button
                variant="outlined"
                size="small"
                startIcon={<VisibilityIcon />}
                onClick={() => onPreview(docItem)}
                sx={{
                  flex: { xs: 1, sm: 'none' },
                  textTransform: 'none',
                  fontWeight: 500,
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: theme.palette.primary.main + '08',
                    color: theme.palette.primary.main
                  }
                }}
              >
                {t('personaldocs_button_view_file')}
              </Button>
              <Button
                variant="contained"
                size="small"
                component="label"
                startIcon={<UploadFileIcon />}
                disabled={isUploading}
                sx={{
                  flex: { xs: 1, sm: 'none' },
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: 'none',
                  '&:hover': { boxShadow: '0 1px 3px rgba(0,0,0,0.12)' }
                }}
              >
                {t('personaldocs_button_replace')}
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  hidden
                  onChange={e => { const f = e.target.files?.[0]; if (f) onUpload(doc.key, f); }}
                />
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              size="small"
              component="label"
              startIcon={<CloudUploadIcon />}
              disabled={isUploading}
              sx={{
                flex: { xs: 1, sm: 'none' },
                textTransform: 'none',
                fontWeight: 500,
                boxShadow: 'none',
                '&:hover': { boxShadow: '0 1px 3px rgba(0,0,0,0.12)' }
              }}
            >
              {t('personaldocs_button_upload')}
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                hidden
                onChange={e => { const f = e.target.files?.[0]; if (f) onUpload(doc.key, f); }}
              />
            </Button>
          )}
        </Box>
      </Box>
      {isUploading && (
        <LinearProgress
          sx={{
            mt: 2,
            height: 3,
            borderRadius: 1,
            backgroundColor: theme.palette.primary.main + '20'
          }}
        />
      )}
    </Paper>
  );
}

// Empty State Component
function EmptyState({ title, description }) {
  const theme = useTheme();
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 8,
        px: 3,
        textAlign: 'center'
      }}
    >
      <Avatar
        sx={{
          width: 64,
          height: 64,
          mb: 2,
          backgroundColor: theme.palette.grey[100],
          color: theme.palette.grey[400]
        }}
      >
        <DescriptionIcon sx={{ fontSize: 32 }} />
      </Avatar>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: theme.palette.text.primary }}>
        {t('personaldocs_empty_title') || title}
      </Typography>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, maxWidth: 360 }}>
        {t('personaldocs_empty_description') || description}
      </Typography>
    </Box>
  );
}

export default function PersonalDocuments({ userEmail }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));

  // Countries list (only show Mexico and USA)
  const countries = ['Mexico', 'United States of America'];

  // Load Mexico states/cities data (bundled)
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const mexicoData = require('../data/states-cities/Mexico.json');
  // Personal info state
  const [personalInfo, setPersonalInfo] = useState(initialPersonalInfo);
  const [personalErrors, setPersonalErrors] = useState({});
  // Dynamic personal info field definitions
  const [personalInfoFields, setPersonalInfoFields] = useState(defaultPersonalInfoFields);
  // Address state
  const [address, setAddress] = useState(initialAddress);
  // Document upload state
  const [uploadedDocs, setUploadedDocs] = useState({});
  const [uploading, setUploading] = useState({});
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' }); // Add snackbar state
  const [previewDoc, setPreviewDoc] = useState(null);

  const [uploadDocList, setUploadDocList] = useState([]);

  // Fetch required documents configuration from backend (dynamic)
  useEffect(() => {
    fetch(`${API_ENDPOINTS.BASEURL}/api/required-documents`, { credentials: 'include' })
      .then(res => {
        if (!res.ok) throw new Error('No required-docs');
        return res.json();
      })
      .then(docs => {
        if (Array.isArray(docs)) {
          // Normalize to objects with { key, label, required }
          const normalized = docs.map(d => ({ key: d.key, label: d.label || '', required: !!d.required }));
          setUploadDocList(normalized);
        }
      })
      .catch(() => {
        setUploadDocList([]);
      });
  }, []);

  // Fetch personal info field definitions from backend (dynamic)
  useEffect(() => {
    fetch(`${API_ENDPOINTS.BASEURL}/api/required-personalinfo`, { credentials: 'include' })
      .then(res => {
        if (!res.ok) throw new Error('No required-personalinfo');
        return res.json();
      })
      .then(fields => {
        if (Array.isArray(fields) && fields.length > 0) {
          setPersonalInfoFields(fields);
        }
      })
      .catch(() => {
        // keep defaultPersonalInfoFields on error
      });
  }, []);

  // Fetch uploaded documents for checkmark
  useEffect(() => {
    if (!userEmail) return;
    fetch(`${API_ENDPOINTS.BASEURL}/api/user-documents/${userEmail}`, { credentials: 'include' })
      .then(res => res.json())
      .then(docs => {
        const map = {};
        docs.forEach(doc => { map[doc.key] = doc; });
        setUploadedDocs(map);
      });
  }, [userEmail]);

  // Fetch personal info and address for form fields
  useEffect(() => {
    if (!userEmail) return;
    fetch(`${API_ENDPOINTS.BASEURL}/api/user-documents/personalinfo/${userEmail}`, { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        if (data.personalinfo) setPersonalInfo(data.personalinfo);
        if (data.address) setAddress(data.address);
      });
  }, [userEmail]);

  // Handle personal info change and validation (driven by dynamic field definitions)
  const handlePersonalChange = (field, value) => {
    const fieldDef = personalInfoFields.find(f => f.key === field);
    const v = fieldDef && fieldDef.validation ? fieldDef.validation : {};
    let val = value;
    let err = '';
    if (v.uppercase) val = value.toUpperCase();
    if (v.digitsOnly) val = value.replace(/\D/g, '');
    if (v.pattern && val) {
      const regex = new RegExp(v.pattern);
      if (!regex.test(val)) err = t(`personaldocs_validation_${field}_invalid`);
    }
    setPersonalInfo(prev => ({ ...prev, [field]: val }));
    setPersonalErrors(prev => ({ ...prev, [field]: err }));
  };

  // Handle address change
  const handleAddressChange = (field, value) => {
    setAddress(prev => ({ ...prev, [field]: value }));
  };

  const mexicoStates = React.useMemo(() => Object.keys(mexicoData || {}), [mexicoData]);
  const citiesForState = React.useMemo(() => (address.state && mexicoData[address.state]) || [], [address.state, mexicoData]);

  // Handle file upload
  const handleFileChange = async (key, file) => {
    setError('');
    if (!ALLOWED_TYPES.includes(file.type)) {
      setSnackbar({ open: true, message: t('personaldocs_error_invalid_file_type'), severity: 'error' });
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('key', key);
    setUploading(prev => ({ ...prev, [key]: true }));

    try {
      const res = await fetch(`${API_ENDPOINTS.BASEURL}/api/upload-document`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      // Read text once (avoid stream already read errors), then try parse JSON
      const text = await res.text();
      let data;
      try {
        data = text ? JSON.parse(text) : {};
      } catch (e) {
        data = { message: text };
      }

      if (!res.ok) {
        let msg;
        if (res.status === 500) {
          msg = t('personaldocs_error_upload_500');
        } else {
          msg = data && (data.message || data.error) ? (data.message || data.error) : t('personaldocs_error_upload_failed');
        }
        setSnackbar({ open: true, message: msg, severity: 'error' });
        setUploading(prev => ({ ...prev, [key]: false }));
        return;
      }

      if (data && data.url) {
        // Prefer the server-stored filename (present in the returned URL)
        let storedFilename = file.name;
        try {
          const urlObj = new URL(data.url);
          storedFilename = decodeURIComponent(urlObj.pathname.split('/').pop());
        } catch (e) {
          // fallback to original file.name
        }
        setUploadedDocs(prev => ({
          ...prev,
          [key]: { key, filename: storedFilename, azure_url: data.url }
        }));
        setSnackbar({ open: true, message: t('personaldocs_success_file_uploaded') || 'File uploaded', severity: 'success' });
      } else {
        const msg = (data && data.message) || t('personaldocs_error_upload_failed');
        setSnackbar({ open: true, message: msg, severity: 'error' });
      }

      setUploading(prev => ({ ...prev, [key]: false }));
    } catch (err) {
      setSnackbar({ open: true, message: err.message || t('personaldocs_error_upload_failed'), severity: 'error' });
      setUploading(prev => ({ ...prev, [key]: false }));
    }
  };

  // Check if all required personal fields are valid (driven by dynamic field definitions)
  const canSavePersonal = personalInfoFields
    .filter(f => f.required)
    .every(f => {
      const val = personalInfo[f.key] || '';
      if (!val) return false;
      if (f.validation && f.validation.pattern) {
        return new RegExp(f.validation.pattern).test(val);
      }
      return true;
    });

  // Save handler (implement API call as needed)
  const handleSavePersonal = () => {
    fetch(`${API_ENDPOINTS.BASEURL}/api/user-documents/personalinfo`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        personalinfo: personalInfo,
        address: address
      })
    })
      .then(res => res.json())
      .then(data => {
        setSnackbar({ open: true, message: data.message || t('personaldocs_success_personal_saved'), severity: 'success' });
      })
      .catch(() => {
        setSnackbar({ open: true, message: t('personaldocs_error_personal_save'), severity: 'error' });
      });
  };

  // Add this function inside your component, below handleSavePersonal:
  const handleSaveAddress = () => {
    fetch(`${API_ENDPOINTS.BASEURL}/api/user-documents/personalinfo`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        address: address
      })
    })
      .then(res => res.json())
      .then(data => {
        setSnackbar({ open: true, message: data.message || t('personaldocs_success_address_saved'), severity: 'success' });
      })
      .catch(() => {
        setSnackbar({ open: true, message: t('personaldocs_error_address_save'), severity: 'error' });
      });
  };

  // Preview and download URLs
  const previewUrl = previewDoc
    ? `${API_ENDPOINTS.BASEURL}/api/user-documents/download/${userEmail}/${previewDoc.filename}?preview=true`
    : '';
  const downloadUrl = previewDoc
    ? `${API_ENDPOINTS.BASEURL}/api/user-documents/download/${userEmail}/${previewDoc.filename}`
    : '';

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, sm: 3, md: 4 }, py: { xs: 3, sm: 4 } }}>
      {/* Header */}
      <Box sx={{ mb: { xs: 3, sm: 4 } }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: theme.palette.text.primary }}>
          {t('personaldocs_title') || 'Personal Documents'}
        </Typography>
        <Typography variant="body1" sx={{ color: theme.palette.text.secondary, maxWidth: 640 }}>
          {t('personaldocs_subtitle') || 'Manage your personal information, address, and required documents for onboarding.'}
        </Typography>
      </Box>

      {/* Personal Information Section */}
      <SectionCard title={t('personaldocs_title_personal_info')} icon={<PersonIcon />}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2.5 }}>
          {personalInfoFields.map(fieldDef => {
            const v = fieldDef.validation || {};
            const labelText = (() => {
              const transKey = `personaldocs_field_${fieldDef.key}`;
              const translated = t(transKey);
              return (translated && translated !== transKey) ? translated : fieldDef.label;
            })();
            const fieldLabel = fieldDef.required ? `${labelText} *` : labelText;

            if (v.type === 'select') {
              return (
                <TextField
                  key={fieldDef.key}
                  select
                  label={fieldLabel}
                  value={personalInfo[fieldDef.key] || ''}
                  onChange={e => handlePersonalChange(fieldDef.key, e.target.value)}
                  fullWidth
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5,
                      backgroundColor: theme.palette.background.paper
                    }
                  }}
                >
                  <MenuItem value="">{t(`personaldocs_field_${fieldDef.key}_placeholder`) !== `personaldocs_field_${fieldDef.key}_placeholder` ? t(`personaldocs_field_${fieldDef.key}_placeholder`) : `-- ${labelText} --`}</MenuItem>
                  {(v.options || []).map(opt => (
                    <MenuItem key={opt} value={opt}>{t(opt) !== opt ? t(opt) : opt}</MenuItem>
                  ))}
                </TextField>
              );
            }
            return (
              <TextField
                key={fieldDef.key}
                label={fieldLabel}
                value={personalInfo[fieldDef.key] || ''}
                inputProps={{ maxLength: v.maxLength || undefined }}
                onChange={e => handlePersonalChange(fieldDef.key, e.target.value)}
                error={!!personalErrors[fieldDef.key]}
                helperText={personalErrors[fieldDef.key]}
                fullWidth
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                    backgroundColor: theme.palette.background.paper
                  }
                }}
              />
            );
          })}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' }, mt: 3 }}>
          <Button
            variant="contained"
            onClick={handleSavePersonal}
            disabled={!canSavePersonal}
            sx={{
              minWidth: { xs: '100%', sm: 140 },
              textTransform: 'none',
              fontWeight: 600,
              py: 1.5,
              boxShadow: 'none',
              '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }
            }}
          >
            {t('personaldocs_button_save_personal')}
          </Button>
        </Box>
      </SectionCard>

      {/* Address Section */}
      <SectionCard title={t('personaldocs_title_address')} icon={<HomeIcon />}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2.5 }}>
          <TextField
            label={t('personaldocs_field_street')}
            value={address.street}
            onChange={e => handleAddressChange('street', e.target.value)}
            fullWidth
            size="small"
            sx={{
              gridColumn: { xs: 'span 1', sm: 'span 2', md: 'span 3' },
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5,
                backgroundColor: theme.palette.background.paper
              }
            }}
          />
          <TextField
            label={t('personaldocs_field_numExt')}
            value={address.numExt}
            onChange={e => handleAddressChange('numExt', e.target.value)}
            fullWidth
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5,
                backgroundColor: theme.palette.background.paper
              }
            }}
          />
          <TextField
            label={t('personaldocs_field_numInt')}
            value={address.numInt}
            onChange={e => handleAddressChange('numInt', e.target.value)}
            fullWidth
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5,
                backgroundColor: theme.palette.background.paper
              }
            }}
          />
          <TextField
            label={t('personaldocs_field_neigborhood')}
            value={address.neigborhood}
            onChange={e => handleAddressChange('neigborhood', e.target.value)}
            fullWidth
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5,
                backgroundColor: theme.palette.background.paper
              }
            }}
          />
          <TextField
            label={t('personaldocs_field_cp')}
            value={address.cp}
            onChange={e => handleAddressChange('cp', e.target.value)}
            fullWidth
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5,
                backgroundColor: theme.palette.background.paper
              }
            }}
          />
          <TextField
            select
            label={t('personaldocs_field_country')}
            value={address.country}
            onChange={e => {
              const val = e.target.value;
              handleAddressChange('country', val);
              // reset state/city when changing country
              handleAddressChange('state', '');
              handleAddressChange('city', '');
            }}
            fullWidth
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5,
                backgroundColor: theme.palette.background.paper
              }
            }}
          >
            {(() => {
              const placeholder = t('personaldocs_field_country_placeholder');
              const label = (placeholder && placeholder !== 'personaldocs_field_country_placeholder') ? placeholder : 'Select';
              return <MenuItem value="" disabled>{label}</MenuItem>;
            })()}
            {countries.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
          {address.country === 'Mexico' ? (
            <>
              <Autocomplete
                options={mexicoStates}
                value={address.state || null}
                onChange={(e, val) => {
                  handleAddressChange('state', val || '');
                  handleAddressChange('city', '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('personaldocs_field_state')}
                    fullWidth
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                        backgroundColor: theme.palette.background.paper
                      }
                    }}
                  />
                )}
              />
              <Autocomplete
                options={citiesForState}
                value={address.city || null}
                onChange={(e, val) => handleAddressChange('city', val || '')}
                disabled={!address.state}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('personaldocs_field_city')}
                    fullWidth
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                        backgroundColor: theme.palette.background.paper
                      }
                    }}
                  />
                )}
              />
            </>
          ) : (
            <>
              <TextField
                label={t('personaldocs_field_state')}
                value={address.state}
                onChange={e => handleAddressChange('state', e.target.value)}
                fullWidth
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                    backgroundColor: theme.palette.background.paper
                  }
                }}
              />
              <TextField
                label={t('personaldocs_field_city')}
                value={address.city}
                onChange={e => handleAddressChange('city', e.target.value)}
                fullWidth
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                    backgroundColor: theme.palette.background.paper
                  }
                }}
              />
            </>
          )}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' }, mt: 3 }}>
          <Button
            variant="contained"
            onClick={handleSaveAddress}
            sx={{
              minWidth: { xs: '100%', sm: 140 },
              textTransform: 'none',
              fontWeight: 600,
              py: 1.5,
              boxShadow: 'none',
              '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }
            }}
          >
            {t('personaldocs_button_save_address')}
          </Button>
        </Box>
      </SectionCard>

      {/* Documents Section */}
      <SectionCard title={t('personaldocs_title_documents')} icon={<DescriptionIcon />}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 1.5 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {uploadDocList.map(doc => {
            const docItem = uploadedDocs[doc.key];
            return (
              <DocumentRow
                key={doc.key}
                doc={doc}
                docItem={docItem}
                uploading={uploading}
                onUpload={handleFileChange}
                onPreview={setPreviewDoc}
              />
            );
          })}
        </Box>

        {uploadDocList.length === 0 && (
          <EmptyState
            title="No documents required"
            description="There are no documents configured for this onboarding process."
          />
        )}
      </SectionCard>

      {/* Document Preview Dialog */}
      <Dialog
        fullScreen={isXs}
        open={!!previewDoc}
        onClose={() => setPreviewDoc(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: isXs ? 0 : 2,
            maxHeight: isXs ? '100%' : '85vh'
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ minWidth: 0, flex: 1, pr: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
              {t('personaldocs_dialog_preview_title')}
            </Typography>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              {previewDoc?.filename}
            </Typography>
          </Box>
          <IconButton onClick={() => setPreviewDoc(null)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, backgroundColor: theme.palette.grey[50] }}>
          <Fade in timeout={200}>
            <Box>
              {previewDoc?.filename?.toLowerCase().endsWith('.pdf') ? (
                <iframe
                  src={previewUrl}
                  title="PDF Preview"
                  width="100%"
                  height={isXs ? 'calc(100vh - 160px)' : 500}
                  style={{ border: 0 }}
                />
              ) : (
                <img
                  src={previewUrl}
                  alt={previewDoc?.filename}
                  style={{ maxWidth: '100%', height: isXs ? 'auto' : 500, display: 'block', margin: '0 auto' }}
                />
              )}
            </Box>
          </Fade>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1.5, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button
            onClick={() => setPreviewDoc(null)}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            {t('close')}
          </Button>
          <Button
            onClick={() => window.open(downloadUrl, '_blank')}
            variant="contained"
            startIcon={<DownloadIcon />}
            sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}
          >
            {t('download')}
          </Button>
        </DialogActions>
      </Dialog>

      <FixedSnackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </Box>
  );
}
